"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocale } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import { useDomainContext } from "@/global-context/domain-context";
import { resolveRouteExperience } from "@/lib/experience/experience-routes";
import type {
  ExperienceResolution,
  ExperienceSource,
  StoredExperience,
} from "@/lib/experience/experience-types";
import {
  readExperienceFromDocumentCookie,
  readExperienceFromSession,
  writeExperienceCookie,
  writeExperienceToSession,
} from "@/lib/experience/experience-storage";

interface ExperienceContextValue extends ExperienceResolution {
  storedExperience: StoredExperience | null;
  setStoredExperience: (experience: StoredExperience) => void;
}

const ExperienceContext = createContext<ExperienceContextValue | null>(null);

export function ExperienceContextWrapper({
  initialStoredExperience,
  children,
}: {
  initialStoredExperience?: StoredExperience | null;
  children: React.ReactNode;
}) {
  const locale = useLocale();
  const pathname = usePathname();
  const domain = useDomainContext();

  const [storedExperience, setStoredExperienceState] =
    useState<StoredExperience | null>(initialStoredExperience ?? null);

  const [storedExperienceSource, setStoredExperienceSource] =
    useState<ExperienceSource | null>(
      initialStoredExperience ? "cookie" : null,
    );

  useEffect(() => {
    const sessionExperience = readExperienceFromSession();

    if (sessionExperience) {
      setStoredExperienceState(sessionExperience);
      setStoredExperienceSource("session");
      return;
    }

    const cookieExperience = readExperienceFromDocumentCookie();

    if (cookieExperience) {
      setStoredExperienceState(cookieExperience);
      setStoredExperienceSource("cookie");
    }
  }, []);

  const resolution = useMemo(() => {
    return resolveRouteExperience({
      pathname: pathname || "/",
      locale,
      domain,
      storedExperience,
      storedExperienceSource,
    });
  }, [pathname, locale, domain, storedExperience, storedExperienceSource]);

  const setStoredExperience = useCallback((experience: StoredExperience) => {
    setStoredExperienceState(experience);
    setStoredExperienceSource("session");
    writeExperienceToSession(experience);
    writeExperienceCookie(experience);
  }, []);

  useEffect(() => {
    if (!resolution.shouldStoreExperience || !resolution.experienceToStore) {
      return;
    }

    setStoredExperience(resolution.experienceToStore);
  }, [
    resolution.shouldStoreExperience,
    resolution.experienceToStore,
    setStoredExperience,
  ]);

  const value = useMemo<ExperienceContextValue>(
    () => ({
      ...resolution,
      storedExperience,
      setStoredExperience,
    }),
    [resolution, storedExperience, setStoredExperience],
  );

  return (
    <ExperienceContext.Provider value={value}>
      {children}
    </ExperienceContext.Provider>
  );
}

export function useExperienceContext() {
  const context = useContext(ExperienceContext);

  if (!context) {
    throw new Error(
      "useExperienceContext must be used inside ExperienceContextWrapper",
    );
  }

  return context;
}
